import { Box, Divider, Typography, useTheme } from '@mui/material'
import React from 'react'
import { TooltipProps } from 'recharts'
import { Payload } from 'recharts/types/component/DefaultTooltipContent'
import { makeStyles } from 'tss-react/mui'
import { ChartView } from 'src/Libs/chart-kit/shared/utils/chartModel'
import { useCustomTranslation } from '@e3dc-react/shell/Hooks/useCustomTranslation'
import { CostSavingChartData } from 'src/Libs/dynamic-power-prices/types'
import { useBuildTimeValueText } from 'src/Libs/dynamic-power-prices/hooks/use-build-time-value-text'
import TooltipItem from './TooltipItem'
import TooltipSummary from './TooltipSummary'
import useTooltipItemValues from 'src/Libs/dynamic-power-prices/hooks/use-tooltip-item-values'
import { hexToRgba } from '@e3dc-react/theme'
import { CostSavingChartTraceKey, CostSavingChartTraceKeys } from 'src/Libs/dynamic-power-prices/cost-saving-chart-trace-keys'

const CUSTOM_XS_BREAKPOINT = 350

const useStyles = makeStyles()(theme => ({
  content: {
    padding: theme.spacing(2),
    backgroundColor: hexToRgba(theme.palette.grey.grey07, 0.95),
    borderRadius: theme.shape.borderRadius,
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1),

    [theme.breakpoints.down('sm')]: {
      '& *': {
        fontSize: 12,
      },
    },

    [theme.breakpoints.down(CUSTOM_XS_BREAKPOINT)]: {
      '& *': {
        fontSize: 10,
      },
    },
  },
  dividerStyles: {
    margin: theme.spacing(1, 0),
    borderColor: theme.palette.grey.grey06,
  },
}))

interface Props extends TooltipProps<number, keyof CostSavingChartData> {
  view: ChartView
  hiddenTraces: CostSavingChartTraceKey[]
  showSummary?: boolean
}

/**
 * Custom tooltip for the cost saving chart
 */
const Tooltip: React.FC<Props> = ({ label, payload, view, hiddenTraces, showSummary = false }) => {
  const typedPayload = payload as Payload<number, keyof CostSavingChartData>[]

  const { classes } = useStyles()
  const { t } = useCustomTranslation()
  const theme = useTheme()
  const tooltipItemValues = useTooltipItemValues({ payload: typedPayload })
  const timeFrameText = useBuildTimeValueText(label, view)

  return (
    <Box className={classes.content}>
      <Typography variant="subtitle1" mb={theme.spacing(1)}>
        {timeFrameText}
      </Typography>
      {!hiddenTraces.includes(CostSavingChartTraceKeys.ConsumptionPrice) && (
        <TooltipItem
          title={
            view === 'day'
              ? t('dynamic-power-prices.cost-saving-chart.tooltip.price')
              : t('dynamic-power-prices.cost-saving-chart.tooltip.average-price')
          }
          color={tooltipItemValues.consumptionPriceColor}
          value2={tooltipItemValues.consumptionPriceValue}
          type="line"
        />
      )}

      {!hiddenTraces.includes(CostSavingChartTraceKeys.ThreeSixtyBatterySavings) && (
        <>
          <Divider className={classes.dividerStyles} />
          <TooltipItem
            title={t('dynamic-power-prices.cost-saving-chart.tooltip.three-sixty-savings-hps')}
            color={tooltipItemValues.threeSixtyBatteryColor}
            value1={tooltipItemValues.threeSixtyBatteryProducedEnergy}
            value2={tooltipItemValues.threeSixtyBatterySavings}
          />
        </>
      )}

      {!hiddenTraces.includes(CostSavingChartTraceKeys.ThreeSixtyWallboxSavings) && (
        <TooltipItem
          title={t('dynamic-power-prices.cost-saving-chart.tooltip.three-sixty-savings-wallbox')}
          color={tooltipItemValues.threeSixtyWallboxColor}
          value1={tooltipItemValues.threeSixtyWallboxProducedEnergy}
          value2={tooltipItemValues.threeSixtyWallboxSavings}
        />
      )}

      {showSummary && (
        <>
          <Divider className={classes.dividerStyles} />
          <TooltipSummary sum={tooltipItemValues.summedSavings} />
        </>
      )}
    </Box>
  )
}

export default Tooltip
